import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate, useParams } from 'react-router';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormFieldValue } from '../forms/FormFieldValue';
import PageContainer from '../layout/PageContainer';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { GET_PRODUCT_BY_ID_QUERY, GET_PRODUCTS_QUERY } from '../../queries/ProductQueries';
import ProductInventoryItemForm, { ProductInventoryItemFormState } from './ProductInventoryItemForm';
import { UPDATE_PRODUCT_INVENTORY_ITEM_MUTATION } from '../../mutations/ProductInventoryItemMutation';
import { PackagingType, type ProductInventoryItem, validate as validateProductInventoryItem } from '../../models/ProductInventoryItem';
import { GET_PRODUCT_INVENTORY_ITEM_BY_ID_QUERY } from '../../queries/ProductInventoryItemQueries';
import { GET_BATCHES_QUERY } from '../../queries/BatchQueries';

function ProductInventoryItemEditForm({
  initialValues,
  onSubmit,
}: {
  initialValues: Partial<ProductInventoryItemFormState['values']>;
  onSubmit: (id: number, formValues: Partial<ProductInventoryItemFormState['values']>) => Promise<boolean>;
}) {
  const { productId, productInventoryItemId } = useParams();
  const navigate = useNavigate();

  const notifications = useNotifications();

  const { data: batchesData } = useQuery(
    GET_BATCHES_QUERY, {
    variables: {
      filter: {
        productId: { eq: +(productId || '0') }
      }
    }
  });

  const [formState, setFormState] = useState<ProductInventoryItemFormState>(() => ({
    values: initialValues,
    errors: {},
  }));

  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = useCallback(
    (newFormValues: Partial<ProductInventoryItemFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = useCallback(
    (newFormErrors: Partial<ProductInventoryItemFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = useCallback(
    (name: keyof ProductInventoryItemFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<ProductInventoryItemFormState['values']>) => {
        const { issues } = validateProductInventoryItem(values);
        setFormErrors({
          ...formErrors,
          [name]: issues?.find((issue) => issue.path?.[0] === name)?.message,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues],
  );

  const handleFormReset = useCallback(() => {
    setFormValues(initialValues);
  }, [initialValues, setFormValues]);

  const handleFormSubmit = useCallback(async (
    formValues: Partial<ProductInventoryItemFormState['values']>) => {
    const { issues } = validateProductInventoryItem(formValues);

    let errors = null;

    if (issues && issues.length > 0) {
      errors = Object.fromEntries(issues.map((issue) => [issue.path?.[0], issue.message]));
    }

    if (errors) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const id = +(productInventoryItemId || '0');

    if (id > 0) {
      try {
        const success = await onSubmit(
          id,
          formValues
        );

        if (success) {
          notifications.show('Product inventory item edited successfully.', {
            severity: 'success',
            autoHideDuration: 3000,
          });

          navigate(`/dashboard/products/${productId}/product-inventory-items`);
        } else {
          notifications.show(
            'Failed to edit product inventory item. Reason: Unknown.',
            {
              severity: 'error',
              autoHideDuration: 3000,
            },
          );
        }
      } catch (editError) {
        notifications.show(
          `Failed to edit product inventory item. Reason: ${(editError as Error).message}`,
          {
            severity: 'error',
            autoHideDuration: 3000,
          },
        );
        throw editError;
      }
    }
  }, [navigate, notifications, onSubmit, productId, productInventoryItemId, setFormErrors]);

  return (
    <ProductInventoryItemForm
      productInventoryItemId={+(productInventoryItemId || '0')}
      productId={+(productId || '0')}
      batches={batchesData?.batches || []}
      formState={formState}
      onFieldChange={handleFormFieldChange}
      onSubmit={handleFormSubmit}
      onReset={handleFormReset}
      submitButtonLabel="Save"
      backButtonPath={`/dashboard/products/${productId}/product-inventory-items`}
    />
  );
}

export default function ProductInventoryItemEdit() {
  const { productId, productInventoryItemId } = useParams();

  const [getProductInventoryItemById] = useLazyQuery(
    GET_PRODUCT_INVENTORY_ITEM_BY_ID_QUERY);

  const [updateProductInventoryItem] = useMutation(
    UPDATE_PRODUCT_INVENTORY_ITEM_MUTATION, {
    refetchQueries: [{ query: GET_PRODUCTS_QUERY }, { query: GET_PRODUCT_BY_ID_QUERY, variables: { id: +(productId || '0') } }],
  });

  const [productInventoryItem, setProductInventoryItem] = useState<ProductInventoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(productInventoryItemId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getProductInventoryItemByIdError } = await getProductInventoryItemById(
          {
            variables: { id }
          });

        setIsLoading(false);
        setError(null);

        if (getProductInventoryItemByIdError) {
          setError(getProductInventoryItemByIdError);
        }

        if (data?.productInventoryItem) {
          setProductInventoryItem(data.productInventoryItem);
        } else {
          setError({ message: 'No product inventory item found' } as Error);
        }

      } catch (showDataError) {
        setIsLoading(false);
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getProductInventoryItemById, productInventoryItemId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = useCallback(
    async (id: number, formValues: Partial<ProductInventoryItemFormState['values']>) => {
      setIsLoading(true);

      const { data: result } = await updateProductInventoryItem({
        variables: {
          input: {
            id: id,
            productId: +(productId || '0'),
            packagingType: formValues.packagingType || PackagingType.JAR,
            quantityOnHand: formValues.quantityOnHand,
            batchId: +(formValues.batchId || '0'),
          }
        }
      });

      setIsLoading(false);

      if (result?.updatedProductInventoryItem) {
        setProductInventoryItem(result.updatedProductInventoryItem);
        return true;
      }

      return false;
    },
    [productId, updateProductInventoryItem],
  );

  const renderEdit = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return productInventoryItem ? (
      <ProductInventoryItemEditForm
        initialValues={productInventoryItem}
        onSubmit={handleSubmit} />
    ) : null;
  }, [isLoading, error, productInventoryItem, handleSubmit]);

  return (
    <PageContainer
      title={`Edit ${productInventoryItem?.product?.name || 'N/A'} Inventory`}
      breadcrumbs={[
        { title: 'Products', path: '/dashboard/products' },
        { title: `${productInventoryItem?.product?.name || 'N/A'}`, path: `/dashboard/products/${productId}` },
        { title: 'Edit Inventory' },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1 }}>{renderEdit}</Box>
    </PageContainer>
  );
}
